/**
 * AuditAI Module Registry
 * 
 * Central registry for all 9 audit modules.
 * Uses SEMANTIC IDs (e.g., 'taxes_fees') instead of numeric IDs (e.g., 'module_6').
 * GUI ordering is controlled by displayOrder, not by ID.
 * 
 * IMPORTANT: All legacy IDs are stored in legacyIds for database migration compatibility.
 */

import type { ModuleDefinition, FileKey, SemanticModuleId } from './types';
import { STANDARD_MODULE_TABS } from './types';

// ============= Module Definitions =============

export const MODULE_DATA_QUALITY: ModuleDefinition = {
  id: 'data_quality',
  displayOrder: 0,
  legacyIds: ['module_0', 'datakvalitet'],
  name: 'Datakvalitet och fullständighet',
  nameKey: 'modules.names.dataQuality',
  shortDescription: 'Kontroll av datakvalitet innan analys',
  description: 'Modulen kontrollerar att underlaget är komplett och konsistent innan någon annan analys körs. Målet är att tidigt upptäcka brister som annars gör efterföljande analyser missvisande (t.ex. datumluckor, brutna verifikationsserier, fel räkenskapsår, eller differenser mellan reskontra och huvudbok). Revisorn får en tydlig gate-status (Passed / PassedWithWarnings / Failed), en lista över identifierade dataproblem med hänvisning till var i datan felet uppstår, samt rekommenderade åtgärder för att komplettera eller rätta underlaget.',
  internalDescription: 'Data Gate – completeness, integrity, period coverage, series gaps',
  requiredFiles: ['GL_SIE'],
  // Alla övriga modulers obligatoriska filer blir rekommenderade här
  // GL_SIE_PREV visas automatiskt tillsammans med GL_SIE via SieMultiFileRow
  recommendedFiles: [
    'AP_OPEN_ITEMS',    // Leverantörsskulder
    'AR_OPEN_ITEMS',    // Kundfordringar
    'BANK_TX',          // Bank och kassa + flera moduler
    'TAX_ACCOUNT',      // Skattekonto
    'VAT_REPORT',       // Moms
  ],
  // Alla modulers valfria filer
  optionalFiles: [
    'COA',              // Kontoplan
    'JOURNAL_META',     // Verifikationsmetadata
    'VENDOR_MASTER',    // Leverantörsregister
    'CUSTOMER_MASTER',  // Kundregister
    'VAT_TX_DETAIL',    // Momstransaktioner
    'AGI_REPORTS',      // Arbetsgivardeklarationer
  ],
  tabs: STANDARD_MODULE_TABS,
  isDataGate: true,
  edgeFunctionName: 'audit-data-quality-analyze',
};

export const MODULE_SIE_ACCOUNTING_BASIS: ModuleDefinition = {
  id: 'sie_accounting_basis',
  displayOrder: 1,
  legacyIds: ['module_1'],
  name: 'Redovisningsgrund',
  nameKey: 'modules.names.sieAccountingBasis',
  shortDescription: 'Analys av SIE-fil och redovisningsprinciper',
  description: 'Modulen analyserar den uppladdade SIE-filen för att identifiera redovisningsgrund, kontoplansstruktur, perioder och nyckeltal. Här presenteras en översikt av räkenskapsåret med jämförelse mot föregående år, nyckeltal som omsättning, bruttomarginal, EBITDA och kassaflöde, samt eventuella signaler och avvikelser som kräver uppföljning.',
  internalDescription: 'SIE parsing, accounting basis, key ratios, analytical review',
  requiredFiles: ['GL_SIE'],
  optionalFiles: ['GL_SIE_PREV'],
  tabs: STANDARD_MODULE_TABS,
  isSieModule: true,
};

export const MODULE_JOURNAL_ENTRIES: ModuleDefinition = {
  id: 'journal_entries',
  displayOrder: 2,
  legacyIds: ['module_2', 'module_1', 'manuella_verifikationer'],
  name: 'Manuella verifikationer och bokslutsrisk',
  nameKey: 'modules.names.journalEntries',
  shortDescription: 'Analys av manuella bokföringsposter',
  description: 'Modulen identifierar och riskklassar manuella bokningar och bokslutsnära justeringar som kan indikera management override, felperiodisering eller andra bokslutsrisker. Målet är att snabbt ge revisorn en prioriterad lista på verifikationer som bör granskas närmare, med motivering baserad på mönster (t.ex. timing nära bokslut, ovanliga kontokombinationer, rundade belopp eller återkommande justeringar). Revisorn får en riskrankad lista över poster/verifikationer, rekommenderade nästa steg (vilket underlag som ska begäras och vilka kontroller som ska göras) samt spårbar referens till ver.nr/rad i huvudboken.',
  internalDescription: 'Journal entries, management override, closing risk',
  requiredFiles: ['GL_SIE'],
  optionalFiles: ['JOURNAL_META', 'COA'],
  tabs: STANDARD_MODULE_TABS,
};

export const MODULE_ACCOUNTS_PAYABLE: ModuleDefinition = {
  id: 'accounts_payable',
  displayOrder: 3,
  legacyIds: ['module_3', 'module_2', 'leverantorsskulder'],
  name: 'Leverantörsskulder',
  nameKey: 'modules.names.accountsPayable',
  shortDescription: 'Analys av leverantörsskulder och betalningar',
  description: 'Modulen analyserar leverantörsreskontra och relaterade bankutbetalningar för att hitta risker kopplade till cut-off, obokförda skulder, sena betalningar och avvikande leverantörsmönster. Målet är att ge revisorn en helhetsbild av leverantörsskuldernas kvalitet och identifiera de leverantörer/poster som kräver stickprov eller fördjupning. Revisorn får åldersanalys, profiler för sena betalningar (frekvens, belopp, duration), flaggade efterföljande betalningar som kan indikera obokförda skulder vid bokslut, samt konkreta åtgärdsförslag och referenser till faktura/betalning.',
  internalDescription: 'AP aging, late payments, unrecorded liabilities',
  requiredFiles: ['AP_OPEN_ITEMS', 'BANK_TX', 'GL_SIE'],
  optionalFiles: ['VENDOR_MASTER', 'JOURNAL_META'],
  tabs: STANDARD_MODULE_TABS,
};

export const MODULE_ACCOUNTS_RECEIVABLE: ModuleDefinition = {
  id: 'accounts_receivable',
  displayOrder: 4,
  legacyIds: ['module_4', 'module_3', 'kundfordringar'],
  name: 'Kundfordringar',
  nameKey: 'modules.names.accountsReceivable',
  shortDescription: 'Analys av kundfordringar och kreditrisk',
  description: 'Modulen analyserar kundreskontra och bankinbetalningar för att bedöma kundfordringarnas existens, värdering och inkuransrisk, inklusive cut-off och efterföljande betalningar. Målet är att hjälpa revisorn att snabbt identifiera gamla eller riskabla fordringar, koncentrationsrisker och poster utan betalningsmönster som kräver uppföljning. Revisorn får en AR-aging med prioriterade öppna poster, matchning mot efterföljande inbetalningar, flaggade avvikelser (t.ex. gamla stora poster utan betalning, många kreditnotor, ovanliga mönster per kund) samt rekommenderade nästa steg och spårbara referenser till faktura/rad.',
  internalDescription: 'AR aging, subsequent receipts, credit risk',
  requiredFiles: ['AR_OPEN_ITEMS', 'BANK_TX', 'GL_SIE'],
  optionalFiles: ['CUSTOMER_MASTER'],
  tabs: STANDARD_MODULE_TABS,
};

export const MODULE_BANK_CASH: ModuleDefinition = {
  id: 'bank_cash',
  displayOrder: 5,
  legacyIds: ['module_5', 'module_4', 'bank_kassa'],
  name: 'Bank och kassa',
  nameKey: 'modules.names.bankCash',
  shortDescription: 'Bankavstämning och kassakontroll',
  description: 'Modulen gör bankavstämning och transaktionsanalys för att hitta okända eller avvikande betalningar/inbetalningar samt säkerställa att bankkonton och bokföring hänger ihop. Målet är att minska manuellt avstämningsarbete och samtidigt ge revisorn en risklista över transaktioner som bör granskas (t.ex. ovanliga mottagare, rundade belopp, helgtransaktioner, saknade bokföringsmatchningar). Revisorn får avstämningsresultat (vad som matchar/inte matchar), en lista över omatchade eller avvikande bankrader med förslag på uppföljning, samt tydliga evidensreferenser till bankrad och relevanta bokföringsposter.',
  internalDescription: 'Bank reconciliation, unknown transactions, cash risk',
  requiredFiles: ['BANK_TX', 'GL_SIE'],
  optionalFiles: [],
  tabs: STANDARD_MODULE_TABS,
};

export const MODULE_TAX_ACCOUNT: ModuleDefinition = {
  id: 'taxes_fees',
  displayOrder: 6,
  legacyIds: ['module_6', 'module_5', 'skattekonto', 'tax_account', 'skatter-avgifter'],
  name: 'Skattekonto',
  nameKey: 'modules.names.taxAccount',
  shortDescription: 'Analys av skattekonto och betalningsbeteende',
  description: 'Modulen analyserar skattekontoutdrag för att identifiera mönster som tyder på bristande skatte-/avgiftsdisciplin, såsom negativa saldon, frekvens och varaktighet av underskott samt räntor och avgifter. Målet är att ge revisorn ett snabbt beslutsunderlag om huruvida skatt/avgifter hanterats korrekt och i tid, och var uppföljning krävs. Revisorn får en sammanställning av negativa perioder (antal, snittbelopp, maxbelopp, antal dagar negativt), flaggade händelser (t.ex. upprepade förseningsavgifter), samt rekommenderade åtgärder och hänvisning till relevanta rader i skattekontot.',
  internalDescription: 'Negative balances, frequency, duration, penalties',
  requiredFiles: ['TAX_ACCOUNT'],
  optionalFiles: ['GL_SIE', 'AGI_REPORTS', 'BANK_TX'],
  tabs: STANDARD_MODULE_TABS,
  edgeFunctionName: 'audit-tax-account-analyze',
  // Analysis configuration
  defaultTolerance: 3,
  supportedFileTypes: ['csv', 'txt'],
  requiresFieldMapping: true,
  preferredProvider: 'azure',
  maxExpectedTokens: 50000,
};

export const MODULE_VAT: ModuleDefinition = {
  id: 'vat',
  displayOrder: 7,
  legacyIds: ['module_7', 'module_6', 'moms'],
  name: 'Moms',
  nameKey: 'modules.names.vat',
  shortDescription: 'Momsavstämning och analys',
  description: 'Modulen stämmer av momsrapport(er) mot bokföringen och analyserar rimlighet i momsnivåer, momskoder och avvikelser över tid. Målet är att upptäcka felaktig momshantering, felklassificeringar eller ovanliga förändringar i effektiv momssats som kan indikera risker i intäkter/kostnader eller regelbrister. Revisorn får avstämningsdifferenser mellan momsrapport och huvudbok, analyser av effektiv momssats och avvikande momsrader/koder, samt en prioriterad lista över poster att kontrollera med rekommenderade nästa steg och spårbar referens till transaktion/rapport-rad.',
  internalDescription: 'VAT reconciliation, reasonability, rate anomalies',
  requiredFiles: ['VAT_REPORT', 'GL_SIE'],
  optionalFiles: ['VAT_TX_DETAIL', 'TAX_ACCOUNT'],
  tabs: STANDARD_MODULE_TABS,
};

export const MODULE_DUPLICATES_ANOMALIES: ModuleDefinition = {
  id: 'duplicates_anomalies',
  displayOrder: 8,
  legacyIds: ['module_8', 'module_7', 'dubletter'],
  name: 'Dubletter och avvikelser',
  nameKey: 'modules.names.duplicatesAnomalies',
  shortDescription: 'Identifiering av dubletter och anomalier',
  description: 'Modulen söker efter dublettfakturor, dublettbetalningar och andra anomalier i leverantörs- och betalningsdata som kan indikera fel, ineffektivitet eller oegentligheter. Målet är att fånga sådant som traditionella stickprov ofta missar genom att analysera hela populationen och ge revisorn en tydlig uppföljningslista. Revisorn får flaggor för möjliga dubletter (t.ex. samma belopp/datum/fakturanr), misstänkta mönster (t.ex. flera leverantörer med samma bankkonto, nya leverantörer med snabb volymuppbyggnad, upprepade rundade belopp), samt rekommenderade verifieringssteg och evidensreferenser för snabb spårning i underlaget.',
  internalDescription: 'Duplicate invoices/payments, anomaly detection',
  requiredFiles: ['AP_OPEN_ITEMS', 'BANK_TX'],
  optionalFiles: ['VENDOR_MASTER', 'GL_SIE'],
  tabs: STANDARD_MODULE_TABS,
};

// ============= Module Registry (Semantic IDs as keys) =============

export const MODULE_REGISTRY: Record<SemanticModuleId, ModuleDefinition> = {
  data_quality: MODULE_DATA_QUALITY,
  sie_accounting_basis: MODULE_SIE_ACCOUNTING_BASIS,
  journal_entries: MODULE_JOURNAL_ENTRIES,
  accounts_payable: MODULE_ACCOUNTS_PAYABLE,
  accounts_receivable: MODULE_ACCOUNTS_RECEIVABLE,
  bank_cash: MODULE_BANK_CASH,
  taxes_fees: MODULE_TAX_ACCOUNT,
  vat: MODULE_VAT,
  duplicates_anomalies: MODULE_DUPLICATES_ANOMALIES,
};

// ============= Ordered Module List (by displayOrder) =============

export const MODULES_ORDERED: ModuleDefinition[] = [
  MODULE_DATA_QUALITY,
  MODULE_SIE_ACCOUNTING_BASIS,
  MODULE_JOURNAL_ENTRIES,
  MODULE_ACCOUNTS_PAYABLE,
  MODULE_ACCOUNTS_RECEIVABLE,
  MODULE_BANK_CASH,
  MODULE_TAX_ACCOUNT,
  MODULE_VAT,
  MODULE_DUPLICATES_ANOMALIES,
];

// ============= Legacy ID Mapping (for database migration) =============

/**
 * Maps any legacy ID to its semantic ID.
 * Use this to normalize IDs from database or legacy code.
 */
const LEGACY_ID_MAP: Record<string, SemanticModuleId> = {};

// Build the legacy ID map from module definitions
for (const module of MODULES_ORDERED) {
  // Map the semantic ID to itself
  LEGACY_ID_MAP[module.id] = module.id;
  // Map all legacy IDs to the semantic ID
  for (const legacyId of module.legacyIds) {
    LEGACY_ID_MAP[legacyId] = module.id;
  }
}

// ============= Core Functions =============

/**
 * Normalize any module ID (legacy or semantic) to the canonical semantic ID.
 * This is the PRIMARY function for handling module IDs from database or user input.
 * 
 * @param rawId - Any module ID (e.g., 'module_6', 'skattekonto', 'taxes_fees')
 * @returns The semantic ID (e.g., 'taxes_fees'), or the original ID if not found
 * 
 * @example
 * normalizeModuleId('module_6') // => 'taxes_fees'
 * normalizeModuleId('skattekonto') // => 'taxes_fees'
 * normalizeModuleId('taxes_fees') // => 'taxes_fees'
 * normalizeModuleId('unknown') // => 'unknown' (passthrough)
 */
export function normalizeModuleId(rawId: string): SemanticModuleId | string {
  return LEGACY_ID_MAP[rawId] || rawId;
}

/**
 * Get module by ID (handles both semantic and legacy IDs)
 */
export function getModule(moduleId: string): ModuleDefinition | undefined {
  const normalizedId = normalizeModuleId(moduleId);
  return MODULE_REGISTRY[normalizedId as SemanticModuleId];
}

/**
 * Get module by display order (0-8)
 */
export function getModuleByDisplayOrder(displayOrder: number): ModuleDefinition | undefined {
  return MODULES_ORDERED.find(m => m.displayOrder === displayOrder);
}

/**
 * Get all aliases (legacy IDs + semantic ID) for a module
 * Useful for database queries that need to match multiple ID formats.
 */
export function getModuleAliases(moduleId: string): string[] {
  const module = getModule(moduleId);
  if (!module) return [moduleId];
  return [module.id, ...module.legacyIds];
}

/**
 * Check if module requires data gate (Module 0) to pass first
 */
export function requiresDataGate(moduleId: string): boolean {
  const module = getModule(moduleId);
  return module !== undefined && !module.isDataGate;
}

/**
 * Get all files required across multiple modules (deduplicated)
 */
export function getAllRequiredFiles(moduleIds: string[]): FileKey[] {
  const files = new Set<FileKey>();
  moduleIds.forEach(id => {
    const module = getModule(id);
    if (module) {
      module.requiredFiles.forEach(f => files.add(f));
    }
  });
  return Array.from(files);
}

/**
 * Get all optional files across multiple modules (deduplicated)
 */
export function getAllOptionalFiles(moduleIds: string[]): FileKey[] {
  const files = new Set<FileKey>();
  moduleIds.forEach(id => {
    const module = getModule(id);
    if (module) {
      module.optionalFiles.forEach(f => files.add(f));
    }
  });
  return Array.from(files);
}

/**
 * Check which modules can use a specific file
 */
export function getModulesForFile(fileKey: FileKey): ModuleDefinition[] {
  return MODULES_ORDERED.filter(m => 
    m.requiredFiles.includes(fileKey) || m.optionalFiles.includes(fileKey)
  );
}

/**
 * Get the Data Gate module
 */
export function getDataGateModule(): ModuleDefinition {
  return MODULE_DATA_QUALITY;
}

/**
 * Check if all required files for a module are present
 */
export function hasAllRequiredFiles(moduleId: string, uploadedFileKeys: FileKey[]): boolean {
  const module = getModule(moduleId);
  if (!module) return false;
  return module.requiredFiles.every(f => uploadedFileKeys.includes(f));
}

// ============= Legacy Compatibility (deprecated - use normalizeModuleId) =============

/**
 * @deprecated Use getModule() instead - handles both legacy and semantic IDs
 */
export function getModuleByLegacyId(legacyId: string): ModuleDefinition | undefined {
  return getModule(legacyId);
}

/**
 * @deprecated Use normalizeModuleId() instead
 */
export function mapLegacyToNewModuleId(legacyId: string): string | undefined {
  const module = getModule(legacyId);
  return module?.id;
}

/**
 * Get module configuration by module type (legacy API)
 * @deprecated Use getModule() instead
 */
export function getModuleConfig(moduleType: string): ModuleDefinition {
  const module = getModule(moduleType);
  if (!module) {
    throw new Error(`Unknown module type: ${moduleType}`);
  }
  return module;
}

/**
 * Get edge function name for a module type (legacy API)
 * @deprecated Use getModule(moduleType)?.edgeFunctionName instead
 */
export function getEdgeFunctionForModule(moduleType: string): string {
  const module = getModuleConfig(moduleType);
  return module.edgeFunctionName || 'analyze-module-data';
}

/**
 * Check if a module exists in the registry
 */
export function isValidModuleType(moduleType: string): boolean {
  return getModule(moduleType) !== undefined;
}

/**
 * Get all registered module types (semantic IDs)
 */
export function getAllModuleTypes(): SemanticModuleId[] {
  return Object.keys(MODULE_REGISTRY) as SemanticModuleId[];
}

/**
 * Re-export AIProvider type for backward compatibility
 */
export type { AIProvider, SemanticModuleId } from './types';
